import { TellescopeModelFromName } from "@tellescope/tanstack-query";

type Block = TellescopeModelFromName<"custom_dashboards">["blocks"][number];

export interface ResolveBlockRefArgs {
  blocks: Block[] | undefined;
  blockId?: string;
  blockIndex: number;
}

export interface ResolvedBlockRef {
  block: Block | undefined;
  index: number;
}

/**
 * Resolves a block within a view's `blocks` array by its generated `info.id`
 * first, falling back to the array position only when no id was supplied.
 *
 * A block is addressed primarily by `blockId` (the UUID stored at `block.info.id`).
 * `blockIndex` is used only for legacy blocks/references that predate id generation,
 * i.e. when `blockId` is absent.
 *
 * A supplied `blockId` that matches nothing resolves to no block, deliberately. The
 * id identifies a specific block, so when it is gone — deleted by a teammate, with a
 * data-sync refresh bringing in the shortened list — the position it used to occupy
 * now holds an unrelated block. Falling back to that position would silently swap one
 * block for another (Payments where Vitals was) with nothing to signal the switch.
 *
 * @returns the matched `block` and its resolved `index`, or `{ block: undefined, index: -1 }`
 *   when the identity does not resolve.
 */
export const resolveBlockRef = ({ blocks, blockId, blockIndex }: ResolveBlockRefArgs): ResolvedBlockRef => {
  if (!blocks) return { block: undefined, index: -1 };

  if (blockId !== undefined) {
    const index = blocks.findIndex((block) => block.info?.id === blockId);
    return index === -1 ? { block: undefined, index: -1 } : { block: blocks[index], index };
  }

  const block = blocks.at(blockIndex);
  return block ? { block, index: blockIndex } : { block: undefined, index: -1 };
};
